// Global Instructions Rule Applied!

import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'

// Read shape of one Email Recipients row.
//
// Single superset model — same approach as email_campaign_grid_model.ts. The
// compose form's Recipients tab grid uses the lean subset of keys; the
// override drawer reads body_override_html / subject_override / merge_data
// from the same row. Detail-only keys are .optional() so a list-grid row
// passes validation without them. .strict() rejects unknown keys.
//
// Every optional field is also .nullable() — Phase 2 gotcha #1 — because the
// Python `zon` validator (add_nullables=True) materializes missing optional
// fields as JSON null, which a bare .optional() Zod schema rejects (it
// accepts undefined, NOT null). Every row would silently fail validation
// otherwise.

export const EmailRecipientGridModelSchema = z
  .object({
    // Always present (list grid)
    email_recipients__id: z.string(),
    email_recipients__email_campaign_id: z.string(),
    email_recipients__account_id: z.string().nullable().optional(),
    email_recipients__contact_id: z.string().nullable().optional(),
    email_recipients__email_address: z.string(),
    email_recipients__role: z.string(),
    email_recipients__status: z.string(),
    email_recipients__account_name: z.string(),
    // Phase 4b: accounts.team_name on the recipient row so team-mode grids
    // can show the team label without a separate accounts fetch. Empty
    // string when not set; the dispatch's resolveTeamName helper applies
    // the accounts.name fallback at render.
    email_recipients__account_team_name: z.string().nullable().optional(),
    email_recipients__contact_first_name: z.string(),
    email_recipients__contact_last_name: z.string(),
    email_recipients__has_body_override: z.boolean(),
    email_recipients__has_subject_override: z.boolean(),
    email_recipients__date_modified: z.string(),

    // Per-row payload + dispatch state (optional + nullable)
    email_recipients__merge_data: z.unknown().nullable().optional(),
    email_recipients__subject_override: z.string().nullable().optional(),
    email_recipients__body_override_html: z.string().nullable().optional(),
    email_recipients__provider_message_id: z.string().nullable().optional(),
    email_recipients__error: z.string().nullable().optional(),
    email_recipients__sent_at: z.string().nullable().optional(),
    email_recipients__delivered_at: z.string().nullable().optional()
  })
  .strict()

export const EmailRecipientGridModelFields = makeFieldKeys(EmailRecipientGridModelSchema)

export type EmailRecipientGridModel = z.infer<typeof EmailRecipientGridModelSchema>

export function validateEmailRecipientGridModel(data: unknown): EmailRecipientGridModel {
  try {
    return validateWithZodSchema(
      EmailRecipientGridModelSchema,
      data,
      'email recipient grid model'
    )
  } catch (error) {
    throw new Error(`Email recipient grid model validation failed: ${error}`)
  }
}

// ── Resolve response (used by the Recipients-tab "Resolve" call) ─────────────
//
// The backend reuses the existing distribution-list recipient model for
// the resolve payload (it's the same row shape). The compose form maps
// each resolved row → a stage payload row (frozen email_address +
// computed merge_data). Re-export the type alias for self-documenting
// consumer code.
export type { DistributionListRecipient as EmailResolvedRecipient } from './distribution_list_recipients_model.ts'

// ── Stage payload (sent to PUT /email/campaigns/:id/recipients) ──────────────
//
// One element per desired email_recipients row. account_id / contact_id are
// optional (a manually-typed address has no account/contact backing). The
// stage RPC freezes email_address and inserts merge_data verbatim.

export interface EmailRecipientStageItem {
  account_id?: string | null
  contact_id?: string | null
  email_address: string
  role: 'to' | 'cc'
  merge_data: Record<string, unknown>
}

export interface EmailRecipientStagePayload {
  recipients: EmailRecipientStageItem[]
  // Mirrors the resolve params snapshot stored on email_campaigns.
  // Pass {} (or omit) to leave the existing snapshot unchanged.
  resolution_params?: Record<string, unknown>
}

// ── Per-recipient override (PUT /email/recipients/:id/override) ──────────────

export interface EmailRecipientOverridePayload {
  // null/empty string clears the override (reverts to campaign defaults).
  subject_override?: string | null
  body_override_html?: string | null
  role?: 'to' | 'cc'
}
